'use client'

import type { ReactNode } from 'react'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useInViewOnce } from '@/hooks/useInViewOnce'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'

interface PricingSectionClientProps {
  title: string
  subtitle: ReactNode
  bullets: string[]
  ctaTitle: string
  ctaButton: string
  ctaNoSignUp: string
}

const TIMING = {
  subtitle: 150,
  bullets: 400,
  bulletStagger: 100,
  ctaHeading: 800,
  ctaButton: 900,
  note: 1000,
}

const revealBaseClass =
  'transition-all duration-700 ease-out will-change-transform'

export default function PricingSectionClient({
  title,
  subtitle,
  bullets,
  ctaTitle,
  ctaButton,
  ctaNoSignUp,
}: PricingSectionClientProps) {
  const { ref, isInView } = useInViewOnce<HTMLElement>({ threshold: 0.3 })
  const prefersReducedMotion = usePrefersReducedMotion()
  const shouldReveal = prefersReducedMotion || isInView

  return (
    <section
      ref={ref}
      // Pricing sits below multiple sections, so defer paint/layout until it is near viewport.
      className="py-16 md:py-24 px-5 bg-card [content-visibility:auto] [contain-intrinsic-size:1px_800px]"
    >
      <div className="max-w-3xl mx-auto text-center">
        <h2
          className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-3 text-heading ${revealBaseClass} ${
            shouldReveal
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}
        >
          {title}
        </h2>

        <p
          className={`text-sm sm:text-base md:text-lg text-heading-muted mb-8 md:mb-10 max-w-2xl mx-auto ${revealBaseClass} ${
            shouldReveal
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}
          style={
            prefersReducedMotion
              ? undefined
              : { transitionDelay: `${TIMING.subtitle}ms` }
          }
        >
          {subtitle}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-w-2xl mx-auto mb-10 md:mb-12">
          {bullets.map((bullet, index) => (
            <div
              key={bullet}
              className={`flex items-start gap-3 text-left bg-background/50 rounded-xl p-4 border border-border/50 ${revealBaseClass} ${
                shouldReveal
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 -translate-x-5'
              }`}
              style={
                prefersReducedMotion
                  ? undefined
                  : {
                      transitionDelay: `${TIMING.bullets + index * TIMING.bulletStagger}ms`,
                    }
              }
            >
              <div className="shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                <Check className="w-3 h-3 text-primary" strokeWidth={3} />
              </div>
              <span className="text-sm text-foreground">{bullet}</span>
            </div>
          ))}
        </div>

        <h3
          className={`text-lg sm:text-xl md:text-2xl font-semibold text-heading mb-4 ${revealBaseClass} ${
            shouldReveal
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}
          style={
            prefersReducedMotion
              ? undefined
              : { transitionDelay: `${TIMING.ctaHeading}ms` }
          }
        >
          {ctaTitle}
        </h3>

        <div
          className={`${revealBaseClass} ${
            shouldReveal ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.9]'
          } motion-safe:hover:scale-[1.05] motion-safe:active:scale-[0.95]`}
          style={
            prefersReducedMotion
              ? undefined
              : { transitionDelay: `${TIMING.ctaButton}ms` }
          }
        >
          <Button
            asChild
            size="lg"
            className="px-5 py-3 text-sm lg:px-8 lg:py-6 lg:text-lg h-auto hover:shadow-lg transition-shadow"
            onClick={() => {
              void import('@/lib/analytics').then(({ analytics }) => {
                analytics.navigation.ctaClicked(
                  'start_inviting',
                  'pricing_section'
                )
              })
            }}
          >
            <a href="/designs">{ctaButton}</a>
          </Button>
        </div>

        <p
          className={`text-sm sm:text-base text-heading-muted font-medium mt-4 ${revealBaseClass} ${
            shouldReveal
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}
          style={
            prefersReducedMotion
              ? undefined
              : { transitionDelay: `${TIMING.note}ms` }
          }
        >
          {ctaNoSignUp}
        </p>
      </div>
    </section>
  )
}
